// Integrations

import './integrations-callback-urls'
import './integrations-edit'
import './integrations-transfer'

import {on} from 'delegated-events'
import {onKey} from '@github-ui/onfocus'
import {getCheckedSelector} from './personal-access-tokens'

import type {ToggleSwitchElement} from '../../../../components/primer/experimental/toggle-switch-element'
import {updatePermissionSummary} from './personal-access-token-summary'
import {announce} from '@github-ui/aria-live'

interface ActionMenuElement extends HTMLElement {
  disableItem(item: Element | null): void
  enableItem(item: Element | null): void
  checkItem(item: Element | null): void
  getItemById(id: string): HTMLLIElement
}

function enableFormSubmission() {
  const updatePermissionsButton = document.getElementById('js-update-integration-permissions')

  if (updatePermissionsButton) {
    updatePermissionsButton.removeAttribute('disabled')
  }
}

// A list of the currently selected "resource" permissions for the Integration
// based on the permissions drop downs.
//
// E.g. ["issues", "pull_requests", "metadata"]
function selectedResources(): string[] {
  const resources = document.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
    `.js-integration-permissions-selector [id^=integration_permission_]:not([data-permission="none"])${getCheckedSelector()}`,
  )

  return Array.from(resources, resource => resource.getAttribute('data-resource')!)
}

// A NodeList of all the '.js-integration-hook-event' input nodes that are:
// 1. Currently 'checked'
// 2. Belong to the given "resource". E.g. "issues", "pull_requests" etc.
function checkedHookEventsForResource(resource: string | null) {
  if (!resource) return []
  return Array.from(
    document.querySelectorAll<HTMLInputElement>(`.js-integration-hook-event[data-resource~="${resource}"]:checked`),
  )
}

// A list of all the '.js-integration-hook-event' input nodes that are:
// 1. Currently 'checked'
// 2. Belong to resources permissions that are selected. E.g. "Read", "Read &
//    Write" or "Admin".
function checkedHookEventNodes() {
  return selectedResources().flatMap(resource => checkedHookEventsForResource(resource))
}

// Can the given Hook event be hidden based on the chosen Integration
// resource permissions?
// E.g. When Issues is "Read" but Pull Requests is "None" the "Milestones"
// event should still be visible because it applies to *both* resource types.
function hookEventCheckboxCanBeHidden(node: HTMLInputElement): boolean {
  return !checkedHookEventNodes().includes(node)
}

function setNoAccessDisabled(row: HTMLElement, disabled: boolean) {
  const noAccessItem = Array.from(row.querySelectorAll<HTMLLIElement>('[data-targets="action-list.items"]')).find(
    li => li.querySelector('button')?.getAttribute('data-permission') === 'none',
  )
  if (!noAccessItem) return

  const btn = noAccessItem.querySelector<HTMLButtonElement>('button[role="menuitemradio"]')
  if (!btn) return

  // Keep the wrapper purely presentational (no aria-disabled on role="none")
  noAccessItem.removeAttribute('aria-disabled')

  // Visual disabled styling
  noAccessItem.classList.toggle('ActionListItem--disabled', disabled)

  if (disabled) {
    btn.disabled = true
    btn.tabIndex = -1
  } else {
    btn.disabled = false
  }
}

function showAgenticInactiveReason(row: HTMLElement, show: boolean) {
  const el = row.querySelector<HTMLElement>('.js-agentic-inactive-reason')
  if (!el) return
  el.hidden = !show
}

function setAgenticPermissions(isAgentic: boolean) {
  const agenticPermsElement = document.getElementById('js-agentic-integration-permissions')
  if (!agenticPermsElement) return

  const rawAgenticPermissions = agenticPermsElement.getAttribute('data-agentic-integration-permissions')
  let agenticPermissions: Record<string, string> = {}
  if (rawAgenticPermissions) {
    try {
      const parsed: unknown = JSON.parse(rawAgenticPermissions)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        agenticPermissions = parsed as Record<string, string>
      }
    } catch {
      agenticPermissions = {}
    }
  }

  if (isAgentic) {
    for (const [resource, requiredLevel] of Object.entries(agenticPermissions)) {
      const input = document.querySelector(
        `.js-integration-permissions-selector [id^="integration_permission_"][data-resource="${resource}"][data-permission="${requiredLevel}"]`,
      )

      if (input && 'click' in input) {
        ;(input as HTMLElement).click()

        const row = input.closest<HTMLElement>('.js-list-group-item')
        if (row) {
          for (const btn of row.querySelectorAll<HTMLElement>('[id^=integration_permission_]')) {
            btn.removeAttribute('data-mandatory')
          }

          const checked =
            row.querySelector<HTMLElement>('[id^=integration_permission_][aria-checked="true"]') ||
            (input as HTMLElement)
          checked.setAttribute('data-mandatory', 'true')

          row.classList.add('disabled')
          row.setAttribute('aria-disabled', 'true')

          toggleActionMenu(row, false)
          setNoAccessDisabled(row, true)
          showAgenticInactiveReason(row, true)

          row.querySelector<HTMLElement>('.js-selected-label')!.hidden = true
          row.querySelector<HTMLElement>('.js-mandatory-label')!.hidden = false
        }
      }
    }
  } else {
    const rows = document.querySelectorAll<HTMLElement>('.js-list-group-item')
    for (const row of rows) {
      row.classList.remove('disabled')
      row.removeAttribute('aria-disabled')
      row.querySelector<HTMLElement>('.js-selected-label')!.hidden = true
      row.querySelector<HTMLElement>('.js-mandatory-label')!.hidden = true

      toggleActionMenu(row, false)
      setNoAccessDisabled(row, false)
      showAgenticInactiveReason(row, false)

      for (const btn of row.querySelectorAll<HTMLElement>('[id^=integration_permission_]')) {
        btn.removeAttribute('data-mandatory')
      }

      const noAccessButton = row.querySelector<HTMLButtonElement>(
        '[id^="integration_permission_"][data-permission="none"]',
      )
      if (noAccessButton) noAccessButton.click()

      const hiddenInput = row.querySelector<HTMLInputElement>(
        'input[type="hidden"][name^="integration[default_permissions]"]',
      )
      if (hiddenInput) hiddenInput.value = 'none'
    }
  }
}

function setWebhookActive(active: boolean) {
  const checkbox = document.querySelector<HTMLInputElement>('.js-hook-active-checkbox')
  if (!checkbox) return

  if (checkbox.checked !== active) {
    checkbox.checked = active
    // Trigger existing handler that hides/show fields + required state
    checkbox.dispatchEvent(new Event('change', {bubbles: true}))
  }
}

function announceToScreenReader(toggle: ToggleSwitchElement) {
  const announceOn = toggle.getAttribute('data-announce-on') || ''
  const announceOff = toggle.getAttribute('data-announce-off') || ''

  const announcement = toggle.isOn() ? announceOn : announceOff

  if (announcement) announce(announcement)
}

on('click', '.js-agentic-integration-toggle', event => {
  const toggle = event.currentTarget as ToggleSwitchElement
  const isAgentic = toggle.isOn()

  const title = document.querySelector<HTMLElement>('.js-integration-page-title')!
  const submit = document.querySelector<HTMLInputElement>('.js-integration-submit')!

  const text =
    (isAgentic ? title.getAttribute('data-agentic-integration-text') : title.getAttribute('data-integration-text')) ||
    ''

  title.textContent = text
  submit.value = text
  submit.setAttribute('data-disable-with', text)

  const agentSetupRows = document.querySelectorAll<HTMLElement>('.js-agent-setup-row')
  for (const row of agentSetupRows) {
    row.hidden = !isAgentic
  }

  const hiddenInput = document.querySelector<HTMLInputElement>('.js-agentic-integration-toggle-input')
  if (hiddenInput) {
    hiddenInput.value = isAgentic ? '1' : '0'
  }

  setWebhookActive(!isAgentic)

  setAgenticPermissions(isAgentic)

  updatePermissionSummary('repository')
  updatePermissionSummary('organization')
  updatePermissionSummary('user')
  updatePermissionSummary('business')

  announceToScreenReader(toggle)
})

on('change', '.js-integration-permissions-selector [id^=integration_permission_]', ({currentTarget}) => {
  const permission = currentTarget.getAttribute('data-permission') || 'none'
  const resource = currentTarget.getAttribute('data-resource') || ''

  configureHookEvents(permission, resource, currentTarget)
})

on('itemActivated', '#action-selection-list-menu', (event: CustomEvent) => {
  const selectedItem = event.detail.item as HTMLLIElement
  const itemButton = selectedItem.querySelector<HTMLButtonElement>('button')!
  if (!itemButton) return

  const permission = itemButton.getAttribute('data-permission') || 'none'
  const resource = itemButton.getAttribute('data-resource') || ''

  configureHookEvents(permission, resource, itemButton)
})

function configureHookEvents(
  permission: string,
  resource: string,
  currentTarget: (EventTarget & Element) | HTMLButtonElement,
) {
  // One event can apply to multiple resources (E.g. milestones apply to both
  // Issues and Pull Requests). To model this in HTML data-resource can
  // contain one or more resource names separated by spaces. E.g.
  // data-resource="issues pull_requests", which we select using the ~= CSS
  // attribute condition.
  const hookEvents = document.querySelectorAll<HTMLInputElement>(
    `.js-integration-hook-event[data-resource~="${resource}"]`,
  )
  const permissionContainers = document.querySelectorAll(`.js-dropdown-container[data-resource~="${resource}"]`)

  enableFormSubmission()

  if (permission !== 'none') {
    const hookPermissionErrors = document.querySelectorAll(
      `.js-integration-hook-event-permission-error[data-resource~='${resource}']`,
    )
    for (const error of hookPermissionErrors) {
      /* eslint-disable-next-line github/no-d-none */
      error.classList.add('d-none')
    }

    for (const el of hookEvents) {
      el.readOnly = false
    }

    currentTarget.closest<HTMLElement>('.js-list-group-item')!.classList.remove('disabled')
    for (const el of permissionContainers) {
      /* eslint-disable-next-line github/no-d-none */
      el.classList.remove('d-none')
    }
  } else {
    // The integrator has selected "none" as the permission for this resource.
    // Hide all Hook event checkboxes related to this resource:
    for (const el of hookEvents) {
      if (hookEventCheckboxCanBeHidden(el)) {
        // Disable the event hook checkbox
        el.readOnly = true
        el.checked = false

        // Hide the hook checkbox's container
        /* eslint-disable-next-line github/no-d-none */
        el.closest<HTMLElement>('.js-dropdown-container')!.classList.add('anim-fade-in', 'd-none')
      }
    }

    // Disable the current resource permission checkbox so we don't send a
    // value to the server when this form is submitted.
    currentTarget.closest<HTMLElement>('.js-list-group-item')!.classList.add('disabled')

    if (resource === 'single_file') {
      clearSubmittedFields(resource, '.js-single-files-path-list')
    }
  }
}

function clearSubmittedFields(resource: string, selectedList: string) {
  // Hide the resource container
  /* eslint-disable-next-line github/no-d-none */
  document
    .querySelector<HTMLElement>(`.js-dropdown-container[data-resource~='${resource}']`)!
    .classList.add('anim-fade-in', 'd-none')

  document.querySelector<HTMLElement>(selectedList)!.textContent = ''
}

// Toggle the 'required' state of the Hook URL input Field to let users know
// that they need to provide a value when the hook is active.
function toggleHookUrlRequired(active: boolean) {
  const hookUrlField = document.querySelector<HTMLElement>('.js-hook-url-field')
  if (!hookUrlField) return

  hookUrlField.classList.toggle('required')
  if (active) {
    hookUrlField.setAttribute('required', 'required')
  } else {
    hookUrlField.removeAttribute('required')
  }

  const hookUrlFieldContainer = hookUrlField.closest('dl.form-group')
  if (!hookUrlFieldContainer) return

  hookUrlFieldContainer.classList.toggle('required')
}

on('change', '.js-hook-active-checkbox', function (event) {
  const currentTarget = event.currentTarget as HTMLInputElement
  for (const el of document.querySelectorAll<HTMLElement>('.js-integration-hook-enabled')) {
    el.hidden = !currentTarget.checked
  }

  toggleHookUrlRequired(currentTarget.checked)
})

export function toggleMetadataSelectionMenu(mandatory = false) {
  const metadataReadButton = document.querySelector<HTMLButtonElement>(
    '.js-integration-permissions-selector [id=integration_permission_metadata_read]',
  )

  if (!metadataReadButton) return

  const row = metadataReadButton.closest<HTMLElement>('.js-list-group-item')!
  const isSelected = metadataReadButton.getAttribute('aria-checked') === 'true'

  // If metadata is becoming mandatory, ensure "Read" is actually selected
  if (mandatory && !isSelected) {
    metadataReadButton.setAttribute('data-programmatic', 'true')
    metadataReadButton.click()
  }

  // Keep labels in sync
  const selectedNow = metadataReadButton.getAttribute('aria-checked') === 'true'
  handleMetadataRowLabels(row, selectedNow, mandatory)

  // Lock/unlock the menu
  toggleActionMenu(row, mandatory)

  // Mark mandatory for summary counting when locked; clear when unlocked
  if (mandatory) {
    metadataReadButton.setAttribute('data-mandatory', 'true')
  } else {
    metadataReadButton.removeAttribute('data-mandatory')
  }
}

function handleMetadataRowLabels(row: HTMLElement, selected: boolean, mandatory = false) {
  row.querySelector<HTMLElement>('.js-mandatory-label')!.hidden = !mandatory

  const selectedLabel = row.querySelector<HTMLElement>('.js-selected-label')
  if (selectedLabel) {
    selectedLabel.hidden = mandatory || !selected
  }
}

function toggleActionMenu(row: HTMLElement, mandatory = false) {
  const actionMenu: ActionMenuElement = row.querySelector('#action-selection-list-menu')!
  const actionMenuShowButton = row.querySelector<HTMLButtonElement>('.js-action-selection-list-menu-button')!
  const actionMenuTooltip: HTMLElement = actionMenu.querySelector('[id^=tooltip]')!

  if (mandatory) {
    actionMenuShowButton.ariaDisabled = 'true'
    actionMenuShowButton.classList.add('Button--inactive')

    if (actionMenuTooltip) {
      actionMenuTooltip.style.display = 'block'
      actionMenuTooltip.hidden = false
    }
  } else {
    actionMenuShowButton.ariaDisabled = 'false'
    actionMenuShowButton.classList.remove('Button--inactive')

    if (actionMenuTooltip) {
      actionMenuTooltip.style.display = 'none'
      actionMenuTooltip.hidden = false
    }
  }

  // Whatever is already selected in this row (read/write/admin/etc).
  const selectedPermissionEl = row.querySelector<HTMLElement>('[id^=integration_permission_][aria-checked="true"]')
  const selectedPermission = selectedPermissionEl?.getAttribute('data-permission')

  const actionListItems = row.querySelectorAll<HTMLLIElement>('[data-targets="action-list.items"]')
  for (const listItem of actionListItems) {
    const listItemButton = listItem.querySelector<HTMLButtonElement>('button')
    const listItemPermission = listItemButton?.getAttribute('data-permission')

    if (mandatory) {
      actionMenu.disableItem(listItem)

      // Keep the menu's checked state aligned with the already-selected permission
      // without forcing it to "read".
      if (listItemButton && selectedPermission && listItemPermission === selectedPermission) {
        listItemButton.setAttribute('data-programmatic', 'true')
        actionMenu.checkItem(listItem)
      }
    } else {
      actionMenu.enableItem(listItem)
    }
  }
}

function noRepoPermissionSelected(): boolean {
  let noneSelected = true

  const allSelectedRepoPermissions = document.querySelectorAll(
    `.js-integration-permissions-selector [id^=integration_permission_][data-resource-parent=repository]${getCheckedSelector()}`,
  )

  for (const repoPermission of allSelectedRepoPermissions) {
    const permission = repoPermission.getAttribute('data-permission') || 'none'
    const resource = repoPermission.getAttribute('data-resource') || ''
    if (resource !== 'metadata' && permission !== 'none') {
      noneSelected = false
      break
    }
  }
  return noneSelected
}

on('itemActivated', '[data-repository-permissions] #action-selection-list-menu', (event: CustomEvent) => {
  const selectedItem = event.detail.item as HTMLLIElement
  const itemButton = selectedItem.querySelector<HTMLButtonElement>('button')!

  const resourceParent = itemButton.getAttribute('data-resource-parent') || ''
  if (resourceParent !== 'repository') return

  const permission = itemButton.getAttribute('data-permission') || 'none'
  const resource = itemButton.getAttribute('data-resource') || ''

  handleMetadataMenu(resource, permission)
})

// Handle the mandatory repository metadata permission:
//
// When an integrator selects _any_ repository-related permission they are
// _always_ granted the metadata permission.
//
// To make this clear in the UI we select 'Read-only' in the repository
// metadata permissions select menu and disable the menu until _no_ repository
// permissions are selected.
on(
  'change',
  '.js-integration-permissions-selector [id^=integration_permission_][data-resource-parent^=repository]',
  function ({currentTarget}) {
    const permission = currentTarget.getAttribute('data-permission') || 'none'
    const resource = currentTarget.getAttribute('data-resource') || ''

    handleMetadataMenu(resource, permission)
  },
)

function handleMetadataMenu(resource: string, permission: string) {
  if (resource === 'metadata') return

  if (permission === 'none') {
    if (noRepoPermissionSelected()) {
      // If the repository "No Access" permission is selected, and it was the only permission, re-enable the
      // repository-metadata menu so that the user can deselect it if they want.
      toggleMetadataSelectionMenu(false)
    }
  } else {
    toggleMetadataSelectionMenu(true)
  }
}

on('change', '.js-integration-permissions-selector [name^=integration]', enableFormSubmission)
on('change', '.js-integration-hook-event', enableFormSubmission)

on('click', '.js-integration-permissions-selector .js-integration-hook-event', function (event) {
  const target = event.currentTarget as HTMLInputElement

  if (target.readOnly) {
    const sendEvents = target.closest<HTMLElement>('.js-send-events')!
    const errorMessage = sendEvents.querySelector<HTMLElement>('.js-integration-hook-event-permission-error')!
    /* eslint-disable-next-line github/no-d-none */
    errorMessage.classList.remove('d-none')
    event.preventDefault()
  }
})

function addPath() {
  const input = document.querySelector<HTMLInputElement>('.js-single-files input')!
  const template = document.querySelector<HTMLTemplateElement>('.js-single-files template')!
  const list = document.querySelector<HTMLElement>('.js-single-files-path-list')!

  const path = input.value
  if (!path) return

  const item = template.content.cloneNode(true)
  if (!(item instanceof DocumentFragment)) return
  item.querySelector<HTMLInputElement>('input')!.value = path
  item.querySelector<HTMLElement>('.js-single-files-path')!.textContent = path
  input.value = ''

  list.appendChild(item)

  list.closest<HTMLElement>('.Box')!.hidden = false

  enableFormSubmission()
}

onKey('keydown', '.js-single-files input', function (event: KeyboardEvent) {
  // TODO: Refactor to use data-hotkey
  /* eslint eslint-comments/no-use: off */
  /* eslint-disable @github-ui/ui-commands/no-manual-shortcut-logic */
  if (event.key !== 'Enter') return
  event.preventDefault()
  addPath()
  /* eslint-enable @github-ui/ui-commands/no-manual-shortcut-logic */
})

on('click', '.js-single-files .js-path-add', addPath)

on('click', '.js-single-files .js-close', function (event) {
  if (!(event.target instanceof Element)) return
  const item = event.target.closest<HTMLElement>('li')!
  const list = item.parentElement
  if (!list) return
  list.removeChild(item)

  if (list.querySelectorAll('li').length === 0) {
    list.closest<HTMLElement>('.Box')!.hidden = true
  }

  enableFormSubmission()
})
